import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle, ExternalLink, Facebook, Instagram } from "lucide-react";
import { Logo } from "./Logo";
import { useConsent } from "../context/ConsentContext";

const PHONE = "924 020 938";
const PHONE_RAW = "351924020938";
const EMAIL = "liderfavorito@gmail.com";
const ADDRESS = "Rua Cimo de Vila, 1536, 3880-743 São João de Ovar";
const MAPS_QUERY = encodeURIComponent(ADDRESS);
const MAP_EMBED_QUERY = encodeURIComponent("Stand LF, Ovar");

export const Footer = () => {
  const { consent, savePrefs, openManage } = useConsent();
  return (
    <footer id="contact" className="bg-zinc-950 text-white pt-24 pb-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* MAP */}
        <div className="mb-16">
          <span className="text-primary font-bold text-xs tracking-[0.3em] uppercase">Onde estamos</span>
          <h3 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mt-2 mb-6">Visite o nosso stand</h3>
          <div className="rounded-3xl overflow-hidden border border-white/10 h-[360px]">
            {consent.functional ? (
              <iframe
                data-testid="location-map"
                title="Mapa STAND LF"
                src={`https://maps.google.com/maps?q=${MAP_EMBED_QUERY}&z=15&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div data-testid="map-consent-placeholder" className="h-full flex flex-col items-center justify-center text-center gap-3 bg-white/5 px-6">
                <MapPin className="w-8 h-8 text-primary" />
                <p className="text-zinc-300 text-sm max-w-sm">O mapa do Google necessita de cookies funcionais. Ative para ver a nossa localização.</p>
                <button data-testid="enable-map" onClick={() => savePrefs({ ...consent, functional: true })} className="rounded-full bg-primary text-primary-foreground font-semibold px-5 py-2 text-sm transition-transform hover:-translate-y-0.5">Ativar mapa</button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-1">
            <Logo light size="sm" />
            <p className="text-zinc-400 text-sm leading-relaxed mt-4">
              O seu stand de confiança em São João de Ovar. Viaturas selecionadas com garantia e transparência. Servimos toda a região de Ovar, Aveiro e Porto.
            </p>
            <div className="flex items-center gap-3 mt-5">
              <a data-testid="social-facebook" href="https://www.facebook.com/lfveiculosautomoveis.automoveis" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-300 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a data-testid="social-instagram" href="https://www.instagram.com/stand_lf" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-300 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-widest text-zinc-500 mb-5">Localização</h4>
            <a
              data-testid="footer-location"
              href={`https://maps.google.com/?q=${MAPS_QUERY}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-start gap-3 text-zinc-300 hover:text-primary transition-colors"
            >
              <MapPin className="w-5 h-5 mt-0.5 shrink-0" />
              <span className="text-sm">Rua Cimo de Vila, 1536<br />3880-743 São João de Ovar</span>
            </a>
          </div>

          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-widest text-zinc-500 mb-5">Contactos</h4>
            <div className="space-y-4 text-sm">
              <a data-testid="footer-phone" href={`tel:+${PHONE_RAW}`} className="flex items-center gap-3 text-zinc-300 hover:text-primary transition-colors">
                <Phone className="w-4 h-4" /> {PHONE}
              </a>
              <a data-testid="footer-email" href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-zinc-300 hover:text-primary transition-colors">
                <Mail className="w-4 h-4" /> {EMAIL}
              </a>
              <a data-testid="footer-whatsapp" href={`https://wa.me/${PHONE_RAW}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-300 hover:text-primary transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-widest text-zinc-500 mb-5">Legal</h4>
            <div className="space-y-4 text-sm">
              <a
                data-testid="footer-livro-reclamacoes"
                href="https://www.livroreclamacoes.pt/inicio"
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-zinc-300 hover:text-primary transition-colors"
              >
                Livro de Reclamações <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <Link data-testid="footer-privacy" to="/politica-privacidade" className="block text-zinc-300 hover:text-primary transition-colors">
                Política de Privacidade
              </Link>
              <Link data-testid="footer-legal" to="/resolucao-litigios" className="block text-zinc-300 hover:text-primary transition-colors">
                Informação Legal · RAL
              </Link>
              <Link data-testid="footer-cookies" to="/politica-cookies" className="block text-zinc-300 hover:text-primary transition-colors">
                Política de Cookies
              </Link>
              <button data-testid="footer-cookie-settings" onClick={openManage} className="block text-left text-zinc-300 hover:text-primary transition-colors">
                Definições de cookies
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-16 pt-8">
          <h4 className="font-heading font-bold text-sm uppercase tracking-widest text-zinc-500 mb-4">Zonas que servimos</h4>
          <div className="flex flex-wrap gap-3 mb-6">
            <Link data-testid="footer-local-ovar" to="/carros-usados-ovar" className="text-sm text-zinc-300 hover:text-primary transition-colors underline-offset-4 hover:underline">Carros usados em Ovar</Link>
            <span className="text-zinc-700">·</span>
            <Link data-testid="footer-local-aveiro" to="/carros-usados-aveiro" className="text-sm text-zinc-300 hover:text-primary transition-colors underline-offset-4 hover:underline">Carros usados em Aveiro</Link>
            <span className="text-zinc-700">·</span>
            <Link data-testid="footer-local-porto" to="/automoveis-usados-porto" className="text-sm text-zinc-300 hover:text-primary transition-colors underline-offset-4 hover:underline">Automóveis usados no Porto</Link>
          </div>
          <p data-testid="footer-seo-text" className="text-zinc-500 text-xs leading-relaxed max-w-4xl">
            A <strong className="text-zinc-400 font-semibold">STAND LF</strong> é o seu stand de automóveis em <strong className="text-zinc-400 font-semibold">São João de Ovar</strong>, especializado em <strong className="text-zinc-400 font-semibold">carros usados e seminovos</strong> revistos e com garantia. Compre o seu <strong className="text-zinc-400 font-semibold">carro usado em Ovar</strong> com total transparência: viaturas de todas as marcas — Volkswagen, BMW, Mercedes, Renault, Peugeot, Audi e muitas outras. Servimos toda a região de <strong className="text-zinc-400 font-semibold">Aveiro, Porto e norte de Portugal</strong>, com <strong className="text-zinc-400 font-semibold">financiamento</strong> e <strong className="text-zinc-400 font-semibold">retoma</strong> do seu automóvel. Procura um <strong className="text-zinc-400 font-semibold">stand de carros em Aveiro</strong> ou <strong className="text-zinc-400 font-semibold">automóveis usados perto de Ovar</strong>? Visite-nos na Rua Cimo de Vila, 1536, ou contacte-nos por telefone e WhatsApp.
          </p>
        </div>

        <div className="border-t border-white/10 mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-zinc-500 text-xs">© {new Date().getFullYear()} STAND LF. Todos os direitos reservados.</p>
          <div className="flex items-center gap-4">
            <p className="text-zinc-600 text-xs">Rua Cimo de Vila, 1536, 3880-743 São João de Ovar</p>
            <Link data-testid="footer-admin" to="/login" aria-label="Área reservada" className="text-zinc-800 hover:text-zinc-500 text-xs transition-colors">·</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
